import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { SportProvider } from '../../models/interfaces/SportProvider';
import { Match } from '../../models/interfaces/Match';
import { Stat } from '../../models/interfaces/Stat';
import { RosterEntry } from '../../models/interfaces/RosterEntry';
import { User } from '../../models/interfaces/User';

function statTimestampString(timestamp) {
    const format = (val) => `0${Math.floor(val)}`.slice(-2);
    const minutes = (timestamp % 3600) / 60;

    return [minutes, timestamp % 60].map(format).join(':');
}

export function generateScoreSheetPDF(sportProvider: SportProvider, match: Match, user ?: User) {
    var doc = new jsPDF();
    const getOfficialSignaturePromise = new Promise<string | undefined>((resolve) => {
        if (user) {
            return match.adminForUser(user).then((matchAdmin) => {
                return matchAdmin.getResultVerificationPromise().then(resultVerificiation => {
                    resolve(resultVerificiation.verificationObject)
                }).catch(() => {
                    resolve(undefined)
                })
            })
        } else {
            resolve(undefined)
        }
    })
    return getOfficialSignaturePromise.then(loadedImageData => {
        return match.statProvider.once().then((matchSegmentStats) => {
            // Game Information Table
            // Date, Time
            // White: Team Name
            // Dark: Team Name
            autoTable(doc, {
                startY: 20,
                head: [['Date', 'Time', 'White', 'Dark']],
                body: [
                    [
                        match.date.toLocaleDateString(),
                        match.date.toLocaleTimeString(),
                        match.lightCapTeam.name,
                        match.darkCapTeam.name,
                    ],
                ],
            });

            const gameTableLastY = (doc as any).lastAutoTable.finalY + 5;

            // Parse out the stats into maps
            const teams = [match.lightCapTeam, match.darkCapTeam];
            var teamScoreMap = new Map<string, number>();
            var teamTimeoutMap = new Map<string, Stat[]>();
            const timeoutTypes = sportProvider.timeoutStatTypes;
            var teamPlayerStatMap = new Map<string, Map<string, Stat[]>>();
            var playerMap = new Map<string, Map<string, RosterEntry>>();
            const scoreRows = matchSegmentStats.reverse().map((matchSegmentStat) => {
                const matchSegment = matchSegmentStat.matchSegment;
                const scores = teams.map((team) => {
                    return matchSegmentStat.stats.reduce((currentCount, stat) => {
                        if (stat.team && stat.team.id === team.id) {
                            if (
                                timeoutTypes.find((timeoutType) => {
                                    return timeoutType.databaseValue === stat.description.dataType.databaseValue;
                                })
                            ) {
                                var currentTimeoutArray = teamTimeoutMap.get(team.id);
                                if (!currentTimeoutArray) {
                                    currentTimeoutArray = [];
                                }
                                currentTimeoutArray.push(stat);
                                teamTimeoutMap.set(team.id, currentTimeoutArray);
                            }
                            const player = stat.player;
                            if (player) {
                                var playerStatMap = teamPlayerStatMap.get(team.id);
                                if (!playerStatMap) {
                                    playerStatMap = new Map<string, Stat[]>();
                                }
                                var playerArray = playerStatMap.get(player.id);
                                if (!playerArray) {
                                    playerArray = [];
                                }
                                playerArray.push(stat);
                                playerStatMap.set(player.id, playerArray);
                                teamPlayerStatMap.set(team.id, playerStatMap);
                                var teamPlayerMap = playerMap.get(team.id);
                                if (!teamPlayerMap) {
                                    teamPlayerMap = new Map<string, RosterEntry>();
                                }
                                teamPlayerMap.set(player.id, player);
                                playerMap.set(team.id, teamPlayerMap);
                            }

                            const updatedCount = currentCount + stat.description.dataType.scoreValue;
                            var currentTotalScore = teamScoreMap.get(team.id);
                            if (!currentTotalScore) {
                                currentTotalScore = 0;
                            }
                            currentTotalScore += stat.description.dataType.scoreValue;
                            teamScoreMap.set(team.id, currentTotalScore);
                            return updatedCount;
                        } else {
                            return currentCount;
                        }
                    }, 0);
                });
                return [matchSegment.displayName].concat(
                    scores.map((value) => {
                        return value.toString();
                    })
                );
            });
            const totalRow = ['Total'].concat(
                teams.map((team) => {
                    const teamScore = teamScoreMap.get(team.id);
                    if (teamScore) {
                        return teamScore.toString();
                    } else {
                        return '0';
                    }
                })
            );

            // Quarter Goal Results
            // Goal Count by Quarter
            autoTable(doc, {
                head: [
                    ['Team Scores', '', ''],
                    ['Quarter', 'White', 'Dark'],
                ],
                body: scoreRows.concat([totalRow]),
                startY: gameTableLastY,
                margin: { right: 107 },
            });

            const goalY = (doc as any).lastAutoTable.finalY + 5;
            const numberOfTimeouts = Array.from(teamTimeoutMap.entries()).reduce(
                (currentMax, teamEntry) => {
                    const timeoutArray = teamEntry[1];
                    if (currentMax < timeoutArray.length) {
                        return timeoutArray.length;
                    } else {
                        return currentMax;
                    }
                },
                0
            );

            let timeoutY = 0;
            if (numberOfTimeouts > 0) {
                //  Timeouts
                // Column [White, Dark]
                // Rows: [Match Segment, Time]
                let timeoutArray: string[][] = [];
                for (let i = 0; i < numberOfTimeouts; i++) {
                    let timeoutEntry: string[] = [''];
                    teams.forEach((team) => {
                        const teamTimeouts = teamTimeoutMap.get(team.id);
                        if (teamTimeouts && i < teamTimeouts.length) {
                            const timeout = teamTimeouts[i];
                            timeoutEntry.push(
                                `${timeout.matchSegment.displayName} - ${statTimestampString(
                                    timeout.timestamp
                                )}`
                            );
                        } else {
                            timeoutEntry.push('');
                        }
                    });
                    timeoutArray.push(timeoutEntry);
                }
                autoTable(doc, {
                    head: [['Timeouts', 'White', 'Dark']],
                    body: timeoutArray,
                    startY: gameTableLastY,
                    margin: { left: 107 },
                });
                timeoutY = (doc as any).lastAutoTable.finalY + 5;
            }

            var playerTableY = Math.max(goalY, timeoutY);

            if (loadedImageData) {
                doc.text('Official Signature:', 15, playerTableY + 5)
                doc.addImage(loadedImageData, 'PNG', 15, playerTableY + 7, 100, 50)
                playerTableY = playerTableY + 70
            } 

            teams.forEach((team) => {
                // Team roster tables
                const playerStatMap = teamPlayerStatMap.get(team.id);
                if (!playerStatMap) {
                    return;
                }

                const teamPlayerMap = playerMap.get(team.id);
                if (!teamPlayerMap) {
                    return;
                }
                const playerArray = Array.from(teamPlayerMap.entries())
                    .sort((left, right) => {
                        const leftPlayer = left[1];
                        const rightPlayer = right[1];
                        return leftPlayer.capNumber.sortValue - rightPlayer.capNumber.sortValue;
                    })
                    .map((entry) => {
                        return entry[1];
                    });
                const matchSegmentColumns = matchSegmentStats.map((matchSegmentStat) => {
                    return matchSegmentStat.matchSegment;
                });

                // header: Team Name
                // columns: [Cap Number, Player Name, Personal Fouls, Goal by Quarter, Total Goals]
                const teamHeaderRow = [team.name, '', '', 'Goals'].concat(
                    matchSegmentColumns.map(() => {
                        return '';
                    })
                );
                const matchSegmentTextColumns = matchSegmentColumns.map((matchSegment) => {
                    return matchSegment.displayName;
                });
                const playerHeaderRow = ['Cap', 'Name', 'Fouls']
                    .concat(matchSegmentTextColumns)
                    .concat(['Total']);

                autoTable(doc, {
                    startY: playerTableY,
                    head: [teamHeaderRow, playerHeaderRow],
                    body: playerArray.map((player) => {
                        var playerStatArray = playerStatMap.get(player.id);
                        if (!playerStatArray) {
                            playerStatArray = [];
                        }
                        const playerFoulArray = playerStatArray.filter((stat) => {
                            return sportProvider.personalFoulTypes.find((foulType) => {
                                return foulType.databaseValue === stat.description.dataType.databaseValue;
                            });
                        });
                        const playerFoulRow =
                            playerFoulArray.length > 0
                                ? playerFoulArray
                                    .map((stat) => {
                                        return `${stat.abbreviation}${stat.matchSegment.displayName}`;
                                    })
                                    .join(', ')
                                : '';

                        const playerGoalArray = playerStatArray.filter((stat) => {
                            return stat.scoreValue > 0;
                        });

                        const matchSegmentGoalColumns = matchSegmentColumns.map((matchSegment) => {
                            return playerGoalArray
                                .filter((goalStat) => {
                                    return (
                                        goalStat.matchSegment.databaseValue ===
                                        matchSegment.databaseValue
                                    );
                                })
                                .reduce((currentCount, stat) => {
                                    return currentCount + stat.scoreValue;
                                }, 0)
                                .toString();
                        });
                        return [
                            player.capNumber.displayName,
                            `${player.name.firstName} ${player.name.lastName}`,
                        ]
                            .concat(playerFoulRow)
                            .concat(matchSegmentGoalColumns)
                            .concat([
                                playerGoalArray
                                    .reduce((currentValue, stat) => {
                                        return currentValue + stat.scoreValue;
                                    }, 0)
                                    .toString(),
                            ]);
                    }),
                });
                playerTableY = (doc as any).lastAutoTable.finalY + 5;
            });

            const allStats = matchSegmentStats.reduce((currentStats, matchSegmentStat) => {
                return currentStats.concat(
                    matchSegmentStat.stats.reverse().filter((stat) => {
                        return sportProvider.shouldDisplayStatTypeInScoreSheet(stat.description.dataType);
                    })
                );
            }, new Array<Stat>());

            // Timeline table
            // header: Game Progress
            // columns: [Time, White, Dark, Note, Current Score (if a goal event)]
            var whiteScoreValue = 0;
            var darkScoreValue = 0;
            autoTable(doc, {
                startY: playerTableY,
                head: [
                    ['', '', 'Timeline', '', '', ''],
                    ['Time', 'White', 'Dark', 'Type', 'Note', 'Score'],
                ],
                body: allStats.map((stat) => {
                    const playerText = stat.player ? `#${stat.player.capNumber.displayName}` : 'C';
                    var whiteTeamText = '';
                    if (stat.team && stat.team.id === teams[0].id) {
                        whiteTeamText = playerText;
                        whiteScoreValue += stat.scoreValue;
                    }
                    var darkTeamText = '';
                    if (stat.team && stat.team.id === teams[1].id) {
                        darkTeamText = playerText;
                        darkScoreValue += stat.scoreValue;
                    }

                    return [
                        `${stat.matchSegment.displayName} - ${statTimestampString(stat.timestamp)}`,
                        whiteTeamText,
                        darkTeamText,
                        stat.displayName,
                        stat.remarks || '',
                        `${whiteScoreValue} - ${darkScoreValue}`,
                    ];
                }),
            });
            return Promise.resolve((doc as any).lastAutoTable.finalY + 5)
        }).then(() => {
            doc.save(
                `scoresheet_${match.lightCapTeam.abbreviation}-vs-${match.darkCapTeam.abbreviation}`
            );
            return Promise.resolve();
        }) 
    })
}
